import { Box } from '@mui/material';
import { type MouseEvent } from 'react';

import { isTruthy } from 'common';

interface DrawerResizeHandleProps {
  label: string;
  isResizing: boolean;
  width: number;
  minimumWidth: number;
  maximumWidth: number;
  onStartResizing: (event: MouseEvent) => void;
  onResetWidth: () => void;
  onChangeWidth: (width: number) => void;
}

export function DrawerResizeHandle(props: DrawerResizeHandleProps) {
  const {
    label,
    isResizing,
    width,
    minimumWidth,
    maximumWidth,
    onStartResizing,
    onResetWidth,
    onChangeWidth,
  } = props;

  return (
    <Box
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      aria-valuenow={width}
      aria-valuemin={minimumWidth}
      aria-valuemax={maximumWidth}
      tabIndex={0}
      onMouseDown={(event) => {
        if (event.button === 0) {
          onStartResizing(event);
        }
      }}
      onDoubleClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onResetWidth();
      }}
      onKeyDown={(event) => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          event.stopPropagation();
          if (event.key === 'Home') {
            onResetWidth();
          } else {
            onChangeWidth(
              event.key === 'End' ? maximumWidth : width + (event.key === 'ArrowLeft' ? 20 : -20)
            );
          }
        }
      }}
      sx={{
        position: 'absolute',
        top: 0,
        left: '-16px',
        bottom: 0,
        width: '33px',
        cursor: 'col-resize',
        pointerEvents: 'all',
        zIndex: 1,
        backgroundClip: 'padding-box',
        backgroundImage: (theme) =>
          `linear-gradient(90deg, transparent 16px, ${theme.palette.primary.dark} 17px, transparent 20px)`,
        transition: (theme) =>
          theme.transitions.create('opacity', {
            easing: theme.transitions.easing.sharp,
            duration: isTruthy(isResizing)
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        opacity: isTruthy(isResizing) ? 1 : 0,
        '&:hover, &:focus-visible': { opacity: 1 },
      }}
    />
  );
}
